import React from 'react'
import { Alert, FormControlLabel, Switch, TextField } from '@mui/material'
import { AI_CHAT_POLICY_MAX, aiChatSupport } from '../sessionSettings'

const COMPARE_TYPES = ['InitialFinal', 'InitialFinalCSS', 'InitialFinalJS']

// AI chat on/off plus the optional AI Chat Policy, shared by the creation wizard's last
// step and the student-layout Task and Unit Test tab so both explain the same trade-offs
// the same way. Purely controlled - the caller owns the values and decides when to save.
//
// `type` is the session's CURRENT type: for the Python family, "on" means the session
// becomes 'Vizmental' (see sessionSettings.buildAiChatUpdate). That is an internal type
// name, so it is deliberately not shown to instructors here.
const AiChatSettings = ({
  type,
  enabled,
  onEnabledChange,
  policy = '',
  onPolicyChange,
  onPolicyBlur,
  disabled = false,
  dense = false,
  // 'wizard' (nothing saved until Finish) or 'tab' (each change saves immediately, on a
  // session students may already be in).
  context = 'wizard',
  // Only the policy field - no on/off switch. The Session Setting tab uses this: AI chat
  // is switched on and off from the ENABLE AI CHAT switch in the page header there.
  policyOnly = false,
  // Id of the switch's caption; set when several of these are on one page (the experiment
  // step has one per condition), since element ids must be unique.
  helpId = 'sv-ai-settings-help',
}) => {
  const support = aiChatSupport(type)

  if (support === 'unavailable') {
    return (
      <Alert severity="info">
        AI chat isn&apos;t available for this language — students here get peer chat with their
        group instead, and that is managed from the Groups tab.
      </Alert>
    )
  }

  const isCompare = COMPARE_TYPES.includes(type)
  const policyLength = policy.length

  const policyField = (
    <>
      <TextField
        label="AI Chat Policy (optional)"
        placeholder={'Hard rules the assistant must follow, e.g.\n- Never write a complete solution.\n- Only discuss loops and conditionals.'}
        value={policy}
        onChange={(e) => onPolicyChange(e.target.value.slice(0, AI_CHAT_POLICY_MAX))}
        onBlur={onPolicyBlur}
        disabled={disabled}
        fullWidth
        multiline
        minRows={dense ? 3 : 5}
        maxRows={dense ? 10 : 12}
        autoFocus={policyOnly}
        inputProps={{ maxLength: AI_CHAT_POLICY_MAX }}
        helperText={`${policyLength}/${AI_CHAT_POLICY_MAX} · Added to the assistant's instructions for every student message. Only instructors can see it.`}
      />
      {isCompare && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          In this exercise the assistant&apos;s reply is used as the student&apos;s code. A rule like
          &ldquo;never write code&rdquo; breaks it — write rules about content, not format.
        </Alert>
      )}
    </>
  )

  if (policyOnly) return <div className="sv-ai-settings">{policyField}</div>

  return (
    <div className="sv-ai-settings">
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            disabled={disabled}
            inputProps={{ 'aria-describedby': helpId }}
          />
        }
        label="AI chat for students"
      />
      <div id={helpId} className="sv-caption" style={{ marginTop: 0 }}>
        {enabled
          ? 'Students get an AI assistant next to their editor that sees their code and test results.'
          : 'Students work without an AI assistant.'}
      </div>

      {context === 'tab' && type === 'Vizmental' && (
        <div className="sv-caption">
          Off hides the assistant&apos;s pane from students. It does not end the session.
        </div>
      )}

      {!enabled && support === 'required' && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This exercise is built around the AI: its instructions ask students to describe the
          target to the assistant, and the chat is the only thing in the right-hand column. With
          chat off, that column is empty.
        </Alert>
      )}

      {enabled && <div style={{ marginTop: dense ? 10 : 16 }}>{policyField}</div>}
    </div>
  )
}

export default AiChatSettings
